import React, { useEffect, useState, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ChevronRightIcon, BuildingIcon, UserIcon, TargetIcon, DatabaseIcon, FileTextIcon, SettingsIcon, CalendarIcon, MailIcon, BriefcaseIcon, UsersIcon, PresentationIcon, HandshakeIcon, LinkedinIcon, SlackIcon, GlobeIcon, SearchIcon, InfoIcon, AlertCircleIcon, BarChart3Icon, HeartIcon, ThumbsUpIcon, ThumbsDownIcon, PlusIcon, CheckCircleIcon, XCircleIcon, RefreshCwIcon } from 'lucide-react';
// Step components
import { CompanyPersonaSetup } from '../components/onboarding/CompanyPersonaSetup';
import { UserPersonaRole } from '../components/onboarding/UserPersonaRole';
import { MeetingGoalDefinition } from '../components/onboarding/MeetingGoalDefinition';
import { DataSourceConnections } from '../components/onboarding/DataSourceConnections';
import { FirstTestBrief } from '../components/onboarding/FirstTestBrief';
import { PersonalizationLoop } from '../components/onboarding/PersonalizationLoop';
export function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState({
    // Company Persona
    companyName: '',
    companyUrl: '',
    companyPersona: null,
    // User Persona
    userRole: '',
    // Meeting Goals
    meetingGoals: [],
    // Data Sources
    connectedSources: {
      googleCalendar: false,
      gmail: false,
      salesforce: false,
      hubspot: false,
      slack: false,
      linkedin: false
    },
    // Test Brief
    sampleBrief: null,
    // Personalization
    usefulElements: [],
    missingElements: []
  });
  const totalSteps = 6;
  const updateOnboardingData = (key, value) => {
    setOnboardingData(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setLoading(true);
      // Simulate API calls or processing
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setLoading(false);
      }, 800);
    } else {
      // Complete onboarding
      navigate('/dashboard');
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        return !onboardingData.companyName || !onboardingData.companyUrl;
      case 2:
        return !onboardingData.userRole;
      case 3:
        return onboardingData.meetingGoals.length === 0;
      case 4:
        return !Object.values(onboardingData.connectedSources).some(value => value);
      case 5:
      case 6:
        return false;
      default:
        return false;
    }
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <CompanyPersonaSetup data={onboardingData} updateData={updateOnboardingData} data-id="element-2542" />;
      case 2:
        return <UserPersonaRole data={onboardingData} updateData={updateOnboardingData} data-id="element-2543" />;
      case 3:
        return <MeetingGoalDefinition data={onboardingData} updateData={updateOnboardingData} data-id="element-2544" />;
      case 4:
        return <DataSourceConnections data={onboardingData} updateData={updateOnboardingData} data-id="element-2545" />;
      case 5:
        return <FirstTestBrief data={onboardingData} updateData={updateOnboardingData} data-id="element-2546" />;
      case 6:
        return <PersonalizationLoop data={onboardingData} updateData={updateOnboardingData} data-id="element-2547" />;
      default:
        return null;
    }
  };
  const getStepIcon = step => {
    switch (step) {
      case 1:
        return <BuildingIcon className="h-5 w-5" data-id="element-2548" />;
      case 2:
        return <UserIcon className="h-5 w-5" data-id="element-2549" />;
      case 3:
        return <TargetIcon className="h-5 w-5" data-id="element-2550" />;
      case 4:
        return <DatabaseIcon className="h-5 w-5" data-id="element-2551" />;
      case 5:
        return <FileTextIcon className="h-5 w-5" data-id="element-2552" />;
      case 6:
        return <SettingsIcon className="h-5 w-5" data-id="element-2553" />;
      default:
        return null;
    }
  };
  const getStepTitle = step => {
    switch (step) {
      case 1:
        return 'Company Profile';
      case 2:
        return 'Your Role';
      case 3:
        return 'Meeting Goals';
      case 4:
        return 'Data Sources';
      case 5:
        return 'Sample Brief';
      case 6:
        return 'Personalization';
      default:
        return '';
    }
  };
  return <div className="w-full min-h-screen bg-[#1E2A38] text-[#F5F7FA] font-sans" data-id="element-2554">
      {/* Header */}
      <header className="w-full py-4 px-6 md:px-12 flex items-center justify-between border-b border-[#F5F7FA] border-opacity-10" data-id="element-2555">
        <div className="flex items-center" data-id="element-2556">
          <Link to="/" className="flex items-center" data-id="element-2557">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ov4W5y1TwYUNeHUCdRzqBe/Prepz.io_Logo_2.png" alt="Prepz.io Logo" className="h-24 w-auto" data-id="element-2558" />
          </Link>
        </div>
        <div className="text-sm text-[#F5F7FA] text-opacity-70" data-id="element-2559">
          Need help?{' '}
          <a href="#" className="text-[#C8F169] hover:underline" data-id="element-2560">
            Contact support
          </a>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2561">
        {/* Progress Indicator */}
        <div className="mb-8" data-id="element-2562">
          <div className="flex items-center justify-between" data-id="element-2563">
            {Array.from({
            length: totalSteps
          }).map((_, index) => <Fragment key={index} data-id="element-2564">
                <div className="flex flex-col items-center" data-id="element-2565">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep > index + 1 ? 'bg-[#C8F169] text-[#1E2A38]' : currentStep === index + 1 ? 'bg-[#C8F169] bg-opacity-20 text-[#C8F169] border border-[#C8F169]' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA] text-opacity-40'}`} data-id="element-2566">
                    {currentStep > index + 1 ? <CheckIcon className="h-5 w-5" data-id="element-2567" /> : getStepIcon(index + 1)}
                  </div>
                  <span className={`text-xs mt-2 ${currentStep === index + 1 ? 'text-[#C8F169] font-medium' : 'text-[#F5F7FA] text-opacity-60'}`} data-id="element-2568">
                    {getStepTitle(index + 1)}
                  </span>
                </div>
                {index < totalSteps - 1 && <div className={`flex-1 h-0.5 mx-2 ${currentStep > index + 1 ? 'bg-[#C8F169]' : 'bg-[#F5F7FA] bg-opacity-10'}`} data-id="element-2569" />}
              </Fragment>)}
          </div>
        </div>
        {/* Step Content */}
        <div className="bg-[#1A2331] rounded-xl p-6 md:p-8 shadow-lg mb-8" data-id="element-2570">
          {renderStepContent()}
        </div>
        {/* Navigation Buttons */}
        <div className="flex justify-between" data-id="element-2571">
          <button onClick={handleBack} disabled={currentStep === 1} className={`flex items-center px-4 py-2 rounded-lg transition-colors ${currentStep === 1 ? 'bg-[#F5F7FA] bg-opacity-5 text-[#F5F7FA] text-opacity-30 cursor-not-allowed' : 'bg-[#F5F7FA] bg-opacity-10 text-[#F5F7FA] hover:bg-opacity-20'}`} data-id="element-2572">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-2573" />
            Back
          </button>
          <button onClick={handleNext} disabled={isNextDisabled() || loading} className={`flex items-center px-6 py-2 rounded-lg transition-colors ${isNextDisabled() ? 'bg-[#3B82F6] bg-opacity-50 text-white cursor-not-allowed' : 'bg-[#3B82F6] hover:bg-opacity-90 text-white'}`} data-id="element-2574">
            {loading ? <>
                <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-2575" />
                Processing...
              </> : <>
                {currentStep === totalSteps ? 'Complete' : 'Continue'}
                <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-2576" />
              </>}
          </button>
        </div>
      </main>
    </div>;
}